import { useEffect, useRef } from 'react'

import { bottomWhiteSpaceSmall } from '@/constants'
import { useQuery } from '@api/hooks'
import { ActionCenterTaskType } from '@domain'
import { Divider, Grid2, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import DirectDealApi, { DirectDealKeys } from '@services/directDeals.api'
import {
	DirectDealsOverview,
	DirectDealStatus,
	DirectDealSummary,
} from '@services/directDeals.types'
import { useQueryClient } from '@tanstack/react-query'
import { largeNumToString } from '@utils/stringHelpers'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import { updateActionCenterCache } from '../directDeals.utils'
import DirectDealCard from './DirectDealCard'

type Props = {
	onSelect: (id: Id) => void
	onChangeStatus: (status: DirectDealStatus | null) => void
	onClose: () => void
}

export default function DirectDealsList({ onSelect, onChangeStatus, onClose }: Props) {
	const { marketBasketId } = useParams()
	const queryClient = useQueryClient()
	const [selectedId, setSelectedId] = useQueryParam('direct-deal', StringParam)
	const [activeTask] = useQueryParam('active-task', StringParam)
	const [statusFilter] = useQueryParam('direct-deals', StringParam)

	// Handle auto-closing when num of pending direct deals goes to 0
	const prevPendingRef = useRef<number | null>(null)

	const { marketBasket } = useMarketBasketContext()
	const currency = marketBasket?.currency

	const { data: summary, isLoading: isLoadingSummary } = useQuery({
		queryKey: DirectDealKeys.summary(marketBasketId),
		queryFn: () => DirectDealApi.summary(marketBasketId),
	})

	const { data: directDeals, isLoading: isLoadingItems } = useQuery({
		queryKey: DirectDealKeys.list(marketBasketId, {
			status: statusFilter as DirectDealStatus,
		}),
		queryFn: () =>
			DirectDealApi.list(marketBasketId, {
				status: statusFilter as DirectDealStatus,
			}),
	})

	// Closes drawer when pending direct deals are completed
	useEffect(() => {
		if (!summary) return

		const completedPendingDirectDeals =
			prevPendingRef.current !== null && prevPendingRef.current > 0 && summary.pending === 0

		if (completedPendingDirectDeals) {
			updateActionCenterCache(queryClient, marketBasketId)
			onClose()
		}

		prevPendingRef.current = summary.pending
	}, [summary, onClose, activeTask, queryClient, marketBasketId])

	// Auto-select first item when switching tabs
	useEffect(() => {
		if (activeTask !== ActionCenterTaskType.DIRECT_DEALS) return
		if (selectedId) return
		const hasDirectDeals = directDeals && directDeals.length > 0
		if (!hasDirectDeals) return

		setSelectedId(directDeals[0].id.toString())
	}, [activeTask, directDeals, selectedId, setSelectedId])

	// Prefetch next direct deal when selectedId changes
	// handling in useEffect so that prefetch runs during initial load (auto selection of id)
	useEffect(() => {
		if (!directDeals || directDeals.length === 0) return

		const currentIndex = directDeals.findIndex((d) => d.id === Number(selectedId))
		const nextItem = directDeals[currentIndex + 1]
		if (!nextItem) return

		queryClient.prefetchQuery({
			queryKey: DirectDealKeys.directDeal(marketBasketId, nextItem.id, { stocked: 'ALL' }),
			queryFn: () => DirectDealApi.directDeal(nextItem.id, { stocked: 'ALL' }),
		})
	}, [selectedId, directDeals, queryClient, marketBasketId])

	return (
		<Stack
			sx={{ width: '48rem', height: '100%', overflow: 'hidden' }}
			data-testid="direct-deals-list"
		>
			<Typography variant="h6" mb={2} px={2} pt={2}>
				Direct Deal Switches
			</Typography>
			<Divider />

			<SummaryUI summary={summary} isLoading={isLoadingSummary} />

			<Divider />

			<ListUI
				items={directDeals ?? []}
				isLoading={isLoadingItems}
				isLoadingSummary={isLoadingSummary}
				summary={summary}
				selectedId={selectedId}
				statusFilter={statusFilter as DirectDealStatus}
				currency={currency}
				onSelect={onSelect}
				onChangeStatus={onChangeStatus}
			/>
		</Stack>
	)
}

type SummaryUIProps = {
	summary: DirectDealsOverview | undefined
	isLoading: boolean
}

export function SummaryUI({ summary, isLoading }: SummaryUIProps) {
	const completed = summary ? summary.accepted + summary.rejected : 0

	if (isLoading) {
		return (
			<Stack direction="row" spacing={2} sx={{ py: 1, px: 2 }}>
				<Skeleton variant="rectangular" width="50%" height={60} />
				<Skeleton variant="rectangular" width="50%" height={60} />
			</Stack>
		)
	}

	return (
		<Grid2 container>
			<Grid2 size={4} sx={{ px: 4, py: 2 }}>
				<Typography variant="body2" fontWeight="bold" color="text.secondary">
					Direct Deals
				</Typography>
				<Typography variant="h6">
					{completed}/{summary?.total ?? 0}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Completed
				</Typography>
			</Grid2>
			<Divider orientation="vertical" flexItem />
			<Grid2 size="grow" sx={{ px: 4, py: 2 }}>
				<Typography variant="body2" fontWeight="bold" color="text.secondary">
					Accepted Savings Opportunity
				</Typography>
				<Typography variant="h6">
					${largeNumToString(summary?.acceptedAnnualTotalSavings ?? 0)}
				</Typography>
			</Grid2>
		</Grid2>
	)
}

type ListUIProps = {
	items: DirectDealSummary[]
	isLoading: boolean
	isLoadingSummary: boolean
	summary: DirectDealsOverview | undefined
	selectedId: Id | null
	statusFilter: DirectDealStatus
	currency: string
	onSelect: (id: Id) => void
	onChangeStatus: (status: DirectDealStatus) => void
}

export function ListUI({
	items,
	isLoading,
	isLoadingSummary,
	summary,
	selectedId,
	statusFilter,
	currency,
	onSelect,
	onChangeStatus,
}: ListUIProps) {
	const hasNoItems = !isLoading && items.length === 0

	return (
		<Stack sx={{ px: 2, flex: 1, overflow: 'hidden' }}>
			<Tabs
				value={statusFilter ?? DirectDealStatus.PENDING}
				onChange={(_, value) => {
					onChangeStatus(value)
					onSelect(null)
				}}
			>
				<Tab
					label={`Review (${isLoadingSummary ? '--' : summary.pending})`}
					value={DirectDealStatus.PENDING}
				/>
				<Tab
					label={`Accepted (${isLoadingSummary ? '--' : summary.accepted})`}
					value={DirectDealStatus.ACCEPTED}
				/>
				<Tab
					label={`Rejected (${isLoadingSummary ? '--' : summary.rejected})`}
					value={DirectDealStatus.REJECTED}
				/>
				<Tab
					label={`All (${isLoadingSummary ? '--' : summary.total})`}
					value={DirectDealStatus.ALL}
				/>
			</Tabs>
			<Stack
				spacing={1.5}
				sx={{ overflowY: 'auto', mt: 2, flex: 1, mr: -2, pr: 2, pb: bottomWhiteSpaceSmall }}
			>
				{isLoading && (
					<>
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
					</>
				)}

				{hasNoItems && (
					<Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
						No {statusFilter ? statusFilter.toLowerCase() : ''} Direct Deal Switches found
					</Typography>
				)}

				{!isLoading &&
					items.map((item) => (
						<DirectDealCard
							key={item.id}
							id={item.id}
							selected={item.id === Number(selectedId)}
							category={item.category}
							manufacturer={item.manufacturerName}
							customer={item.customerName}
							stocking={item.stocking}
							coverage={item.coverage}
							totalSavings={item.annualTotalSavings}
							currency={currency}
							status={item.status}
							onClick={() => onSelect(item.id)}
						/>
					))}
			</Stack>
		</Stack>
	)
}
